import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { config, ensureCognitoConfigured, ensureHostedUiConfigured } from './config.js';

// Re-import guard to keep the helper from being tree-shaken when only oidcSignOut uses it.
void ensureHostedUiConfigured;

export type SocialProvider = 'Google' | 'SignInWithApple';

let manager: UserManager | null = null;

function getManager(): UserManager {
  if (manager) return manager;
  ensureCognitoConfigured();

  // OIDC discovery is exposed by Cognito on the cognito-idp endpoint, NOT on
  // the Hosted UI domain (which 404s on /.well-known/openid-configuration).
  // The discovery doc itself points authorization/token URLs at the Hosted UI.
  manager = new UserManager({
    authority: `https://cognito-idp.${config.cognitoRegion}.amazonaws.com/${config.cognitoUserPoolId}`,
    client_id: config.cognitoClientId,
    redirect_uri: config.oidcRedirectUri,
    post_logout_redirect_uri: config.oidcLogoutRedirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return manager;
}

export interface OidcLoginResult {
  idToken: string;
  accessToken: string;
  email: string;
  sub: string;
}

function toResult(user: User): OidcLoginResult {
  const profile = user.profile as { sub?: string; email?: string };
  if (!profile.sub || !profile.email) {
    throw new Error('OIDC profile missing sub/email');
  }
  if (!user.id_token) throw new Error('OIDC user missing id_token');
  return {
    idToken: user.id_token,
    accessToken: user.access_token,
    email: profile.email,
    sub: profile.sub,
  };
}

export async function startOidcLogin(provider?: SocialProvider): Promise<void> {
  try {
    const m = getManager();
    if (provider) {
      await m.signinRedirect({ extraQueryParams: { identity_provider: provider } });
    } else {
      await m.signinRedirect();
    }
  } catch (err) {
    // Promise rejection from a handler attached to a button onClick is silent
    // by default; surface it so the next failure is debuggable.
    console.error('startOidcLogin failed', err);
    throw err;
  }
}

// React StrictMode in dev mounts effects twice. The OIDC `code` is single-use,
// so the second call would 400 with `invalid_grant`. Cache the in-flight (and
// resolved) promise at module scope so duplicate callers share one exchange.
let pendingCallback: Promise<OidcLoginResult> | null = null;

export async function completeOidcLogin(): Promise<OidcLoginResult> {
  if (!pendingCallback) {
    pendingCallback = (async () => {
      const m = getManager();
      const user = await m.signinRedirectCallback();
      return toResult(user);
    })();
  }
  return pendingCallback;
}

export async function getOidcUser(): Promise<OidcLoginResult | null> {
  const m = getManager();
  const user = await m.getUser();
  if (!user) return null;
  if (user.expired) return null;
  return toResult(user);
}

export async function oidcSignOut(): Promise<void> {
  const m = getManager();
  await m.removeUser();
  // Cognito-style logout endpoint also clears the Hosted UI session.
  const logoutUrl = `${config.cognitoHostedDomain}/logout?client_id=${encodeURIComponent(
    config.cognitoClientId,
  )}&logout_uri=${encodeURIComponent(config.oidcLogoutRedirectUri)}`;
  window.location.assign(logoutUrl);
}
